/**
 * @file        src/lib/nav.js
 * @description Single source of truth for the admin sidebar groups + routes.
 *              Mirrors the approved mock 1:1.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-10 Claude — "Amendment Markup" under B2C/Corporate Flight and Hotel
 *                          (admin migration 078): our per-passenger markup on
 *                          cancellation, void, refund, reschedule and add-ons.
 *   - 2026-10-09 Claude — New "SEO Management" group after Site Management: Page
 *                          SEO, Global SEO, Redirects, robots.txt and SEO Health
 *                          (admin migration 077). Gated on the SAME 'Site
 *                          Management' module as the group above it (see
 *                          Sidebar.jsx GROUP_MODULE) — SEO is part of running the
 *                          storefront, and a new module key would hide it from
 *                          every staff account already granted Site Management.
 *                          Per BRAND, not per audience: no Corporate twin.
 *   - 2026-09-25 Claude — Query Management gained "Group Booking Requests" —
 *                          the storefront's Flight Request / group quote form
 *                          (admin migration 073). Until then a group booking
 *                          request reached this console through NO screen at
 *                          all: it was an email to the brand's support inbox
 *                          and nothing else, so an undelivered send lost the
 *                          lead silently.
 *   - 2026-09-25 Claude — Dropped `big: true` from Package Management. It was a
 *                          hand-applied opt-out of the submenu's old flat 480px
 *                          height cap, granted to the one group that had outgrown
 *                          it — which is exactly why the NEXT group to outgrow it
 *                          (Account Management) got no warning and silently lost
 *                          Communication Log off the bottom. The cap is now
 *                          derived per group, so no opt-out is needed: Package
 *                          Management measures 356px against a derived 576px.
 *   - 2026-09-24 Claude — Account Management gained "Action Centre" (first, above
 *                          Transaction Log — it is the screen you start the day
 *                          on) and "Activity Log". Both are WHOLE-BUSINESS with
 *                          no Corporate twin, the same §9d exception Transaction
 *                          Log, Abandoned Payments and Payment Reconciliation
 *                          already hold: three of the eleven queues the Action
 *                          Centre reads belong to no audience at all, so an
 *                          audience split would file most of the work under
 *                          neither screen. The Overview dashboards keep their
 *                          own audience-scoped panels and are now reachable from
 *                          each other by a tab (AudienceTabs) as well as by
 *                          these two entries — the entries stay, because a tab
 *                          that is also a route has to be linkable.
 *   - 2026-09-22 Claude — Added "B2C Insurance" and "Corporate Insurance" groups
 *                          (Insurance List each). Travel insurance was the one
 *                          product with no admin section at all: a policy sold
 *                          on the storefront could not be listed, opened or
 *                          invoiced from the console. Same B2C/Corporate split
 *                          as Flight and Hotel (§9d) — two screens, one
 *                          component, audience fixed by the route.
 *   - 2026-09-22 Claude — Both Insurance groups gained "Policy Requests": the
 *                          admin queue behind the storefront's "Request
 *                          insurance policy" button (upload the insurer's PDF,
 *                          or refuse with a reason the customer is shown).
 *   - 2026-09-15 Claude — Site Management lost "Home Slider Image". The screen's
 *                          rows never reached a storefront: /api/home-slider was
 *                          module-gated end to end, and both home heroes render
 *                          bundled static art. Removed like Menu Management /
 *                          Bank List; table dropped by admin migration 060.
 *   - 2026-09-22 Claude — Account Management gained "Payment Reconciliation" —
 *                          the money-discrepancy queue (refunds we owe, and
 *                          wallet money a confirmed booking never collected).
 *                          Next to Abandoned Payments because the two answer
 *                          adjacent questions, and whole-business like it and
 *                          the Transaction Log rather than split by audience.
 *   - 2026-09-22 Claude — Account Management gained "Abandoned Payments" —
 *                          the cancelled/failed checkout follow-up queue
 *                          (migration 065), next to Transaction Log. It is
 *                          deliberately NOT part of that ledger: those rows
 *                          mean money moved, and an abandoned checkout
 *                          captured nothing.
 *   - 2026-09-15 Claude — Overview gained "Corporate Dashboard" and Account
 *                          Management gained "Corporate Reports". Both screens
 *                          previously carried a "Customer type" picker (including
 *                          "All (B2C + B2B)") on a page that also served retail —
 *                          the last two places in the console where the two
 *                          audiences shared one screen. They are now separated
 *                          exactly like Booking Lists, discounts, coupons,
 *                          convenience fees and markups.
 *   - 2026-06-22 Claude — Initial. Ported from /Neon_Backend design/index.html.
 *   - 2026-07-27 Claude — Added the "Platform" section (Tenant Management +
 *                          Platform Admin Users). This section stays in the
 *                          data unconditionally — Sidebar.jsx is what hides
 *                          it unless the logged-in user is a super_admin.
 *   - 2026-07-27 Claude — Agent Accounts -> Corporate Accounts; B2B Flight/Hotel
 *                          -> Corporate Flight/Hotel with a new Booking List
 *                          entry each; added Wallet Recharge Log.
 *   - 2026-08-03 Claude — Split the 4 wallet screens out of Account Management
 *                          into their own "Wallet Management" group (same
 *                          /account/* paths — sidebar-only change). "Wallet
 *                          Management" entry renamed "Customer Wallets" to pair
 *                          with "Company Wallets"; the wallet icon moved to the
 *                          new group, Account Management now uses "page".
 *   - 2026-08-04 Claude — Added "FAQ Management" group next to Testimonial
 *                          Management (FAQ List + Add FAQ, CR A2).
 *   - 2026-08-04 Claude — Marketing Management disabled (`disabled: true`) per
 *                          client decision; may return in a future release.
 *   - 2026-08-04 Claude — Added "Credit Dues" (B2B post-pay credit line ledger
 *                          + settlement) under Account Management, next to
 *                          Corporate Accounts where the credit line itself is
 *                          configured.
 *   - 2026-08-05 Claude — Added "Supplier Logs" under Platform (super_admin
 *                          only) — TripJack request/response capture viewer.
 *   - 2026-08-05 Claude — Removed "Add New Customer" from Customer Management;
 *                          the working "Add New Customer" button on the
 *                          Customer List page (`/customers/new`) already
 *                          covers it and the sidebar entry was redundant. Route
 *                          left intact.
 *   - 2026-08-05 Claude — Added "Email Settings" to Account Management
 *                          (per-brand SMTP delivery config + live status).
 *   - 2026-08-05 Claude — Added "Communication Log" to Account Management —
 *                          read-only viewer over every outbound email attempt
 *                          from either backend.
 *   - 2026-08-07 Claude — Removed Package Setting, Sub Category List,
 *                          Inclusion List, and Exclusion List — unused
 *                          scaffolding, same treatment as the Bank
 *                          List/Menu Management removal (see
 *                          047_drop_package_setting_lookups.sql).
 *   - 2026-08-10 Claude — Corporate Flight/Hotel each gained Discount List,
 *                          Coupon List, Used Coupon List, Convenience Fee,
 *                          Cancellation List and Search Log/History — B2B-only
 *                          menu parity with B2C Flight/Hotel, which had all six
 *                          and Corporate had none. Coupons/Discounts/Convenience
 *                          Fee reuse the SAME underlying tables as the B2C
 *                          screens (scoped by `applicable_role`, added to those
 *                          tables by migration 026 but never wired into any
 *                          admin screen until now); Cancellation List and
 *                          Search Log/History reuse the same booking-derived
 *                          endpoints, now actually filtered by audience.
 *   - 2026-08-10 Claude — Marked every remaining "Add …" child `hidden: true`
 *                          (Tenant, Admin User, Blog Category, Blog, Package,
 *                          Package Category, Staff, Testimonial, FAQ, Offer) —
 *                          each list page already carries its own "+ Add"
 *                          button, so the sidebar links were duplicates. Same
 *                          treatment as hotel Add Markup/Add Discount; routes
 *                          stay intact (App.jsx builds them from flatPaths(),
 *                          which keeps hidden entries).
 *   - 2026-08-10 Claude — Account Management gained "Corporate Requests" —
 *                          the storefront self-registration approval queue
 *                          (migration 049), next to Corporate Accounts.
 *   - 2026-08-10 Claude — Tenant Management restructured: the onboarding panels
 *                          that lived stacked on the tenant edit page are now
 *                          their own sidebar entries (Brands, Payment Gateways,
 *                          Supplier Credentials, Admin Portal Branding), each a
 *                          page with a tenant picker (?tenant=<id>). The hidden
 *                          "Add Tenant" entry is gone entirely — its route is
 *                          declared explicitly in App.jsx (same pattern as
 *                          customers/new) and the list page's "+ Add Tenant"
 *                          button is the only way in.
 */
export const NAV = [
  { section: 'Overview', items: [
    // Two audience-separated dashboards, not one with a Customer-type toggle —
    // same rule as every other B2C/Corporate pair below. '/' stays the B2C one
    // because it is also the login landing and 404 bounce target.
    { kind: 'leaf', icon: 'grid', label: 'Dashboard', path: '/' },
    { kind: 'leaf', icon: 'grid', label: 'Corporate Dashboard', path: '/corporate-dashboard' },
  ]},
  { section: 'Platform', items: [
    { kind: 'group', icon: 'server', label: 'Tenant Management', children: [
      { label: 'Tenant List',           path: '/platform/tenants' },
      { label: 'Brands',                path: '/platform/tenants/brands' },
      { label: 'Payment Gateways',      path: '/platform/tenants/gateways' },
      { label: 'Supplier Credentials',  path: '/platform/tenants/suppliers' },
      { label: 'Admin Portal Branding', path: '/platform/tenants/branding' },
      { label: 'Issuing Company',       path: '/platform/tenants/company' },
    ]},
    { kind: 'group', icon: 'users', label: 'Platform Admin Users', children: [
      { label: 'Admin User List', path: '/platform/admin-users' },
      { label: 'Add Admin User',  path: '/platform/admin-users/new', hidden: true },
    ]},
    { kind: 'group', icon: 'server', label: 'Supplier Logs', children: [
      { label: 'Supplier Logs', path: '/platform/supplier-logs' },
    ]},
  ]},
  { section: 'Operations', items: [
    { kind: 'group', icon: 'page', label: 'Account Management', children: [
      { label: 'Action Centre',       path: '/account/action-center' },
      { label: 'Activity Log',        path: '/account/activity-log' },
      { label: 'Transaction Log',     path: '/account/transactions' },
      { label: 'Abandoned Payments',  path: '/account/abandoned-payments' },
      { label: 'Payment Reconciliation', path: '/account/payment-reconciliation' },
      { label: 'Reports',             path: '/account/reports' },
      { label: 'Corporate Reports',   path: '/account/corporate-reports' },
      { label: 'Corporate Accounts',  path: '/account/corporate-accounts' },
      { label: 'Corporate Requests',  path: '/account/corporate-requests' },
      { label: 'Payment Upload',      path: '/account/payment-upload' },
      { label: 'Payment Upload List', path: '/account/payment-list' },
      { label: 'Balance Sheet',       path: '/account/balance' },
      { label: 'Email Settings',      path: '/account/email-settings' },
      { label: 'Communication Log',   path: '/account/communication-log' },
    ]},
    { kind: 'group', icon: 'wallet', label: 'Wallet Management', children: [
      { label: 'Customer Wallets',    path: '/account/wallets' },
      { label: 'Company Wallets',     path: '/account/company-wallets' },
      { label: 'Wallet Settings',     path: '/account/wallet-settings' },
      { label: 'Wallet Recharge Log', path: '/account/wallet-audit-log' },
      // Moved here from Account Management on 2026-10-06: a due is now a
      // property of the wallet, not of a separate B2B credit line, so it is
      // managed where the wallet is. Retail and corporate in one queue.
      { label: 'Outstanding Dues',    path: '/account/credit-dues' },
    ]},
    { kind: 'group', icon: 'plane', label: 'Airport Management', children: [
      { label: 'Airport List', path: '/airports' },
    ]},
    { kind: 'group', icon: 'server', label: 'API Management', children: [
      { label: 'Supplier List',    path: '/api-mgmt/suppliers' },
      { label: 'Log Menu',         path: '/api-mgmt/logs' },
    ]},
    { kind: 'group', icon: 'flight', label: 'B2C Flight', children: [
      { label: 'Booking List',         path: '/flight/bookings' },
      { label: 'Discount List',        path: '/flight/discounts' },
      { label: 'B2C Markup',           path: '/flight/markups' },
      { label: 'Coupon List',          path: '/flight/coupons' },
      { label: 'Used Coupon List',     path: '/flight/coupons/used' },
      { label: 'Convenience Fee',      path: '/flight/convenience' },
      { label: 'Amendment Markup',     path: '/flight/amendment-markups' },
      { label: 'Cancellation List',    path: '/flight/cancellations' },
      { label: 'Flight Search History',path: '/flight/search-history' },
      { label: 'Search Analytics',     path: '/flight/search-analytics' },
    ]},
    { kind: 'group', icon: 'bed', label: 'B2C Hotel', children: [
      { label: 'Booking List',     path: '/hotel/bookings' },
      { label: 'Markup List',      path: '/hotel/markups' },
      // hidden: kept here (and routable) but not rendered in the sidebar — the
      // Markup/Discount list pages already carry their own "+ Add B2C Markup /
      // Discount" button, so a nav link for it is a duplicate. Flip `hidden` off
      // to bring the entry back.
      { label: 'Add Markup',       path: '/hotel/markups/new',   hidden: true },
      { label: 'Discount List',    path: '/hotel/discounts' },
      { label: 'Add Discount',     path: '/hotel/discounts/new', hidden: true },
      { label: 'Coupon List',      path: '/hotel/coupons' },
      { label: 'Used Coupon List', path: '/hotel/coupons/used' },
      { label: 'Convenience Fee',  path: '/hotel/convenience' },
      { label: 'Amendment Markup', path: '/hotel/amendment-markups' },
      { label: 'Cancellation List',path: '/hotel/cancellations' },
      { label: 'Hotel Search Log', path: '/hotel/search-log' },
    ]},
    { kind: 'group', icon: 'flight', label: 'Corporate Flight', children: [
      { label: 'Booking List',         path: '/flight/corporate-bookings' },
      { label: 'Discount List',        path: '/flight/corporate-discounts' },
      { label: 'Corporate Markup',     path: '/flight/b2b-markups' },
      { label: 'Coupon List',          path: '/flight/corporate-coupons' },
      { label: 'Used Coupon List',     path: '/flight/corporate-coupons/used' },
      { label: 'Convenience Fee',      path: '/flight/corporate-convenience' },
      { label: 'Amendment Markup',     path: '/flight/corporate-amendment-markups' },
      { label: 'Cancellation List',    path: '/flight/corporate-cancellations' },
      { label: 'Search History',       path: '/flight/corporate-search-history' },
      { label: 'Search Analytics',     path: '/flight/corporate-search-analytics' },
    ]},
    { kind: 'group', icon: 'bed', label: 'Corporate Hotel', children: [
      { label: 'Booking List',     path: '/hotel/corporate-bookings' },
      { label: 'Discount List',    path: '/hotel/corporate-discounts' },
      { label: 'Corporate Markup', path: '/hotel/b2b-markups' },
      { label: 'Coupon List',      path: '/hotel/corporate-coupons' },
      { label: 'Used Coupon List', path: '/hotel/corporate-coupons/used' },
      { label: 'Convenience Fee',  path: '/hotel/corporate-convenience' },
      { label: 'Amendment Markup', path: '/hotel/corporate-amendment-markups' },
      { label: 'Cancellation List',path: '/hotel/corporate-cancellations' },
      { label: 'Search Log',       path: '/hotel/corporate-search-log' },
    ]},
    { kind: 'group', icon: 'badge', label: 'B2C Insurance', children: [
      { label: 'Insurance List',   path: '/insurance/bookings' },
      { label: 'Policy Requests',  path: '/insurance/policy-requests' },
    ]},
    { kind: 'group', icon: 'badge', label: 'Corporate Insurance', children: [
      { label: 'Insurance List',   path: '/insurance/corporate-bookings' },
      { label: 'Policy Requests',  path: '/insurance/corporate-policy-requests' },
    ]},
    { kind: 'group', icon: 'users', label: 'Customer Management', children: [
      { label: 'Customer List',    path: '/customers' },
    ]},
    // Disabled per client decision 2026-08-04 — may return in a future release.
    // `disabled: true` makes Sidebar.jsx render this group greyed-out and
    // non-interactive (no expand, no navigation); the route/page are left
    // intact so re-enabling is a one-line flip.
    { kind: 'group', icon: 'trend', label: 'Marketing Management', disabled: true, children: [
      { label: 'NewsLetter Email List', path: '/marketing/newsletter' },
    ]},
  ]},
  { section: 'Content', items: [
    { kind: 'group', icon: 'edit', label: 'Blog Management', children: [
      { label: 'Blog Category List', path: '/blog/categories' },
      { label: 'Add Blog Category',  path: '/blog/categories/new', hidden: true },
      { label: 'Blog List',          path: '/blog/posts' },
      { label: 'Add New Blog',       path: '/blog/posts/new', hidden: true },
    ]},
    { kind: 'group', icon: 'box', label: 'Package Management', children: [
      { label: 'Domestic & International Holiday', path: '/package/holiday' },
      { label: 'Package List',                     path: '/package/list' },
      // Hotels are NOT managed by admin inside Package Management — hidden.
      // { label: 'Hotel Setting',                    path: '/package/hotel-settings' },
      { label: 'Category List',                    path: '/package/categories' },
      // { label: 'Hotel List',                       path: '/package/hotels' },
      { label: 'Add Package',                      path: '/package/new',            hidden: true },
      { label: 'Add Package Category',             path: '/package/categories/new', hidden: true },
      // Hotel / room management removed from Package Management (not admin-managed).
      // { label: '＋ Add Hotel Type',                path: '/package/hotel-types/new' },
      // { label: '＋ Add Hotel Amenity',             path: '/package/hotel-amenities/new' },
      // { label: '＋ Add Hotel',                     path: '/package/hotels/new' },
      // { label: '＋ Add Room Type',                 path: '/package/room-types/new' },
      // { label: '＋ Add Room Amenity',              path: '/package/room-amenities/new' },
    ]},
  ]},
  { section: 'System', items: [
    { kind: 'group', icon: 'card', label: 'Payment Management', children: [
      // Editing ('Payment Setting') moved to Platform → Tenant Management →
      // edit a tenant → Payment Gateways. The rows carry the merchant key +
      // salt that sign that tenant's checkout, so which tenant you were
      // editing used to depend on which host you had logged in on. It is now
      // picked explicitly by tenant id, and is super_admin-only — same
      // treatment as the supplier API keys. This entry is the tenant admin's
      // READ-ONLY view of the same rows (see GatewayList.jsx / the backend's
      // deleted write handlers in paymentGateways.routes.js).
      { label: 'Payment Gateways',    path: '/payments/gateways' },
      { label: 'Tax Management',      path: '/payments/tax' },
      { label: 'Invoice Number Series', path: '/payments/invoice-series' },
    ]},
    { kind: 'group', icon: 'users', label: 'Query Management', children: [
      { label: 'Query List',              path: '/queries' },
      // The storefront's "Flight Request" / group booking form. It belongs
      // beside Query List rather than under B2C Flight: it is a customer
      // ASKING us something, not a booking — the same job, one form later —
      // and it shares that screen's 'Query Management' module gate. Whole
      // business, no Corporate twin: the form is reachable without signing in
      // and most of these leads are guests (§9d's Transaction Log exception).
      { label: 'Group Booking Requests', path: '/queries/group-requests' },
    ]},
    { kind: 'group', icon: 'badge', label: 'Staff Management', children: [
      { label: 'Staff List',    path: '/staff' },
      { label: 'Add New Staff', path: '/staff/new', hidden: true },
    ]},
    { kind: 'group', icon: 'users', label: 'Testimonial Management', children: [
      { label: 'Testimonial List', path: '/testimonials' },
      { label: 'Add Testimonial',  path: '/testimonials/new', hidden: true },
    ]},
    { kind: 'group', icon: 'page', label: 'FAQ Management', children: [
      { label: 'FAQ List', path: '/faqs' },
      { label: 'Add FAQ',  path: '/faqs/new', hidden: true },
    ]},
    { kind: 'group', icon: 'trend', label: 'Offer Management', children: [
      { label: 'Offer List',    path: '/offers' },
      { label: 'Add New Offer', path: '/offers/new', hidden: true },
    ]},
    { kind: 'group', icon: 'gear', label: 'Site Management', children: [
      { label: 'Site Setting',      path: '/site/settings' },
      { label: 'Social Links',      path: '/site/social' },
      { label: 'Fare Type List',    path: '/site/fare-types' },
    ]},
    { kind: 'group', icon: 'search', label: 'SEO Management', children: [
      { label: 'Page SEO',    path: '/seo/pages' },
      { label: 'Global SEO',  path: '/seo/settings' },
      { label: 'Redirects',   path: '/seo/redirects' },
      { label: 'robots.txt',  path: '/seo/robots' },
      { label: 'SEO Health',  path: '/seo/audit' },
    ]},
  ]},
];

export function flatPaths() {
  const out = [];
  for (const sec of NAV) for (const it of sec.items) {
    if (it.kind === 'leaf') out.push({ path: it.path, label: it.label });
    else for (const c of it.children) out.push({ path: c.path, label: c.label, group: it.label });
  }
  return out;
}
